import React, { useContext } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { queryClient } from './lib/queryClient';
import Login from './pages/login/Login';
import Register from './pages/register/Register';
import ResetPassword from './pages/reset-password/ResetPassword';
import CampaignCreation from './pages/campaignCreation/CampaignCreation';
import Analytics20 from './pages/analytics/Analytics20';
import ReporteSemanal from './pages/analytics/ReporteSemanal';
import Profile from './pages/Profile/Profile';
import ProtectedLayout from './components/ProtectedLayout';
import { AuthProvider } from './components/context/AuthContext';
import { AdsProvider } from 'components/context/AdsContext';
import { AdsModalProvider } from 'components/context/ModalsContext';
import { ModalsRenderer } from 'components/ModalsRenderer';
import { ChatProvider } from './components/context/ChatContext';
import { GoogleOAuthCallback, FacebookOAuthCallback, MetaOAuthCallback, ErrorBoundary } from 'components';
import { OnboardingPage, CampaignsPage, CampaignDetailsPage, Home } from './pages';
import Optimizaciones from './pages/optimizaciones/Optimizaciones';
import Objetivos from './pages/objetivos/Objetivos';
import Reporting from './pages/reporting/Reporting';

import { SubscriptionProvider } from './components/context/SubscriptionContext';
import { AuthContext } from './components/context/AuthContext';
import {
  OptimizationGenerationToastProvider,
} from './components/context/OptimizationGenerationToastContext';
import OptimizationGenerationToast from './components/ui/OptimizationGenerationToast';
import OptimizationGenerationToastRouteSync from './components/ui/OptimizationGenerationToastRouteSync';
import GoogleAdsFailure from './pages/googleAds/GoogleAdsFailure';
import GoogleAdsSuccess from './pages/googleAds/GoogleAdsSuccess';
import MetaAdsFailure from './pages/metaAds/MetaAdsFailure';
import MetaAdsSuccess from './pages/metaAds/MetaAdsSuccess';
import CampaignWizard from './pages/campaignWizard/CampaignWizard';
import AdsAgeReport from './pages/adsAge/AdsAgeReport';
import MetaCreativeRankingPlayground from './pages/dev/MetaCreativeRankingPlayground';
import OptimizationsV2Mock from './pages/dev/OptimizationsV2Mock';
import AdminBrandPanel from './pages/brandPanel/AdminBrandPanel';
import MediaPlanningPage from './pages/mediaPlanning/MediaPlanningPage';
import PromoCalendarPage from './pages/mediaPlanning/PromoCalendarPage';
import ClientPanel from './pages/brandPanel/ClientPanel';

/** Debe renderizarse bajo AuthProvider para que useContext(AuthContext) sea válido. */
function AppRoutes() {
  const { auth } = useContext(AuthContext);

  return (
    <>
      <Banner />
      <ModalsRenderer />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/google-auth/callback" element={<GoogleOAuthCallback />} />
        <Route path="/facebook-auth/callback" element={<FacebookOAuthCallback />} />
        <Route path="/meta-auth/callback" element={<MetaOAuthCallback />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/google-ads-failure" element={<GoogleAdsFailure />} />
        <Route path="/google-ads-success" element={<GoogleAdsSuccess />} />
        <Route path="/meta-ads-failure" element={<MetaAdsFailure />} />
        <Route path="/meta-ads-success" element={<MetaAdsSuccess />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Protected routes */}
        <Route element={<ProtectedLayout />}>
          <Route path="/home" element={<Home />} />
          <Route path="/objetivos" element={<Objetivos />} />
          <Route path="/optimizador" element={<Optimizaciones />} />
          <Route path="/optimizaciones" element={<Navigate to="/optimizador" replace />} />
          <Route path="/campaign-creation" element={<CampaignCreation />} />
          <Route path="/campaign-wizard" element={<CampaignWizard />} />
          <Route path="/analytics20" element={<Analytics20 />} />
          <Route path="/reporte-semanal" element={<ReporteSemanal />} />
          <Route path="/reporting" element={<Reporting />} />
          <Route path="/ads-age" element={<AdsAgeReport />} />
          <Route path="/ads-performance" element={<MetaCreativeRankingPlayground />} />
          <Route path="/__meta-creative-ranking" element={<MetaCreativeRankingPlayground />} />
          {auth?.user?.is_admin && (
            <Route path="/__mock/optimizations-v2" element={<OptimizationsV2Mock />} />
          )}
          <Route path="/campaigns" element={<CampaignsPage />} />
          <Route path="/campaigns/:platform/:id" element={<CampaignDetailsPage />} />

          {/* Profile routes */}
          <Route path="/profile/*" element={<Profile />} />
          <Route path="/billing" element={<Navigate to="/profile/billing" replace />} />
          <Route path="/security" element={<Profile />} />
          <Route path="/settings" element={<Profile />} />
          <Route path="/help" element={<Profile />} />
          <Route path="/mi-panel" element={<ClientPanel />} />

          {auth?.user?.is_admin && (
            <Route path="/admin/campaigns" element={<CampaignsPage isAdmin />} />
          )}
          {auth?.user?.is_admin && (
            <Route path="/admin/panel" element={<AdminBrandPanel />} />
          )}
          {auth?.user?.is_admin && (
            <Route path="/admin/media-planning" element={<MediaPlanningPage />} />
          )}
          {auth?.user?.is_admin && (
            <Route path="/admin/promo-calendar" element={<PromoCalendarPage />} />
          )}
        </Route>

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/home" replace />} />

        {/* Catch-all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <OptimizationGenerationToastRouteSync />
      <OptimizationGenerationToast />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
        <Router>
          <OptimizationGenerationToastProvider>
            <AdsModalProvider>
              <AdsProvider>
                <SubscriptionProvider>
                  <ChatProvider>
                    <AppRoutes />
                  </ChatProvider>
                </SubscriptionProvider>
              </AdsProvider>
            </AdsModalProvider>
          </OptimizationGenerationToastProvider>
        </Router>
        </QueryClientProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

// Separate component to use the context - banner deshabilitado
function Banner() {
  return null;
}

export default App;